(async () => {
const {
  getElement,
  store,
} = await import("@wordpress/interactivity");

// True when the browser makes the carousel controls from CSS Overflow 5
// pseudo-elements. Then index.css styles them and no extra DOM is
// necessary. Other browsers get real elements with fallback classes.
const supportsNative =
  CSS.supports("selector(::scroll-button(*))") &&
  CSS.supports("selector(::scroll-marker)");

/**
 * Returns the scroll positions of the pages. A page starts at each
 * item with `scroll-snap-align: start`. The breakpoint bands in
 * index.css set that, so these positions always agree with the native
 * scroll markers.
 * @param {Element} scroller
 * @returns {number[]}
 */
const pagePositions = (scroller) => {
  if (scroller.scrollWidth <= scroller.clientWidth + 1) {
    return [ 0 ];
  }

  const base = scroller.getBoundingClientRect().left - scroller.scrollLeft;
  const positions = [];

  for (const item of scroller.querySelectorAll(":scope > figure.wp-block-image")) {
    if (getComputedStyle(item).scrollSnapAlign === "none") continue;
    positions.push(item.getBoundingClientRect().left - base);
  }

  return positions.length ? positions : [ 0 ];
};

/**
 * Returns the index of the page nearest to the scroll offset.
 * @param {number[]} positions
 * @param {number} offset
 * @returns {number}
 */
const nearestPage = (positions, offset) => {
  let nearest = 0;
  positions.forEach((position, index) => {
    if (Math.abs(position - offset) < Math.abs(positions[nearest] - offset)) {
      nearest = index;
    }
  });
  return nearest;
};

/*
 * All programmatic scrolls below use the default behavior. The CSS
 * sets `scroll-behavior: smooth` under motion-safe, so they animate,
 * and they stay instant for users who prefer reduced motion.
 */

/**
 * Adds keyboard support to the focused scroll region. The default
 * arrow key step is small, and the mandatory page snap pulls it back
 * to the start. Pages instead.
 * @param {Element} ref The scroller.
 * @returns {Function} Removes the listener.
 */
const addKeyboard = (ref) => {
  const onKeydown = (event) => {
    if (event.target !== ref) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const maxOffset = ref.scrollWidth - ref.clientWidth;
    const rtl = getComputedStyle(ref).direction === "rtl";

    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      const delta = event.key === "ArrowRight" ? 1 : -1;
      ref.scrollBy({ left: delta * ref.clientWidth });
    } else if (event.key === "Home") {
      ref.scrollTo({ left: 0 });
    } else if (event.key === "End") {
      ref.scrollTo({ left: (rtl ? -1 : 1) * maxOffset });
    } else {
      return;
    }

    event.preventDefault();
  };

  ref.addEventListener("keydown", onKeydown);
  return () => ref.removeEventListener("keydown", onKeydown);
};

/**
 * Builds the fallback scroll buttons and scroll markers.
 * @param {Element} ref The scroller.
 * @param {Object} labels Localized labels from the server.
 * @returns {Function} Removes the controls.
 */
const addControls = (ref, labels) => {
  const doc = ref.ownerDocument;

  // An RTL scroller reports negative scroll offsets.
  const dir = getComputedStyle(ref).direction === "rtl" ? -1 : 1;

  // The wrapper positions the buttons. The Carousel pattern provides
  // it. Makes one when the gallery is used on its own.
  let wrapper = ref.parentElement;
  let ownWrapper = null;
  if (!wrapper.classList.contains("wp-carousel")) {
    ownWrapper = doc.createElement("div");
    ownWrapper.className = "wp-carousel";
    ref.replaceWith(ownWrapper);
    ownWrapper.append(ref);
    wrapper = ownWrapper;
  }

  const makeButton = (side, label, delta) => {
    const el = doc.createElement("button");
    el.type = "button";
    el.className = `scroll-button-${side}`;
    if (label) {
      el.setAttribute("aria-label", label);
    }
    el.setAttribute("aria-controls", ref.id);
    el.addEventListener("click", () => {
      if (el.getAttribute("aria-disabled") === "true") {
        return;
      }
      ref.scrollBy({ left: delta * dir * ref.clientWidth });
    });
    return el;
  };

  const previous = makeButton("left", labels.previous, -1);
  const next = makeButton("right", labels.next, 1);

  const markers = doc.createElement("div");
  markers.className = "scroll-marker-group";
  markers.setAttribute("role", "group");
  if (labels.markers) {
    markers.setAttribute("aria-label", labels.markers);
  }

  wrapper.append(previous, next, markers);

  let positions = [ 0 ];

  // Shows the current page on the markers and the buttons.
  const sync = () => {
    const page = nearestPage(positions, ref.scrollLeft);
    const last = markers.children.length - 1;

    previous.setAttribute("aria-disabled", String(page <= 0));
    next.setAttribute("aria-disabled", String(page >= last));

    Array.from(markers.children).forEach((marker, index) => {
      marker.classList.toggle("target-current", index === page);
      if (index === page) {
        marker.setAttribute("aria-current", "true");
      } else {
        marker.removeAttribute("aria-current");
      }
    });
  };

  // Makes one marker for each page. Runs again when a resize changes
  // the pages.
  const rebuild = () => {
    const found = pagePositions(ref);
    if (found.length === positions.length) {
      positions = found;
      return sync();
    }
    positions = found;

    markers.replaceChildren();
    positions.forEach((position, index) => {
      const marker = doc.createElement("button");
      marker.type = "button";
      marker.className = "scroll-marker";
      if (labels.marker) {
        marker.setAttribute("aria-label", `${labels.marker} ${index + 1}`);
      }
      marker.setAttribute("aria-controls", ref.id);
      marker.addEventListener("click", () => {
        ref.scrollTo({ left: positions[index] });
      });
      markers.append(marker);
    });

    // Hides the controls when there is only one page.
    const hidden = positions.length <= 1;
    previous.hidden = hidden;
    next.hidden = hidden;
    markers.hidden = hidden;

    sync();
  };

  let frame = 0;
  const onScroll = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(sync);
  };
  ref.addEventListener("scroll", onScroll, { passive: true });

  const observer = new ResizeObserver(rebuild);
  observer.observe(ref);

  rebuild();

  return () => {
    observer.disconnect();
    ref.removeEventListener("scroll", onScroll);
    cancelAnimationFrame(frame);
    previous.remove();
    next.remove();
    markers.remove();
    if (ownWrapper) {
      ownWrapper.replaceWith(ref);
    }
  };
};

const { state } = store("core/gallery", {
  callbacks: {
    // Runs once on mount from `data-wp-init--carousel`. Returns the
    // cleanup for unmount.
    carousel() {
      const { ref } = getElement();
      const labels = state.carouselLabels || {};

      const cleanups = [ addKeyboard(ref) ];

      if (!supportsNative) {
        cleanups.push(addControls(ref, labels));
      }

      return () => {
        for (const cleanup of cleanups) {
          cleanup();
        }
      };
    },
  },
});
})();
